import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { usersApi, clubsApi } from '../lib/api';
import { EAPlayerStatsCard } from '../components/EAPlayerStatsCard';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  Shield,
  Coins,
  Gamepad2,
  Users,
  Crown,
  Target,
  Handshake,
  ClipboardList,
  CircleAlert,
  Ban,
} from 'lucide-react';

const UserProfile = () => {
  const { userId } = useParams();
  const { t, i18n } = useTranslation();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const isItalian = i18n.language === 'it';

  const [profile, setProfile] = useState(null);
  const [club, setClub] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const [userRes, statsRes] = await Promise.all([
          usersApi.get(userId),
          usersApi.getStats(userId),
        ]);
        setProfile(userRes.data);
        setStats(statsRes.data);
        if (userRes.data.club_id) {
          try {
            const clubRes = await clubsApi.get(userRes.data.club_id);
            setClub(clubRes.data);
          } catch {}
        }
      } catch (err) {
        toast.error(isItalian ? 'Utente non trovato' : 'User not found');
        navigate(-1);
      }
      setLoading(false);
    };
    loadProfile();
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen ept-bg-ambient flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00F0FF]/30 border-t-[#00F0FF] rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) return null;

  const roleColors = {
    super_admin: 'bg-[#FF1744]/20 text-[#FF1744] border-[#FF1744]/30',
    admin: 'bg-[#FF9100]/20 text-[#FF9100] border-[#FF9100]/30',
    captain: 'bg-[#00F0FF]/20 text-[#00F0FF] border-[#00F0FF]/30',
    moderator: 'bg-[#AA00FF]/20 text-[#AA00FF] border-[#AA00FF]/30',
    player: 'bg-[#00E676]/20 text-[#00E676] border-[#00E676]/30',
  };

  const roleLabels = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    captain: isItalian ? 'Capitano' : 'Captain',
    moderator: isItalian ? 'Moderatore' : 'Moderator',
    player: isItalian ? 'Giocatore' : 'Player',
  };

  const InfoRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-center gap-3 py-3 border-b border-white/5 last:border-0">
      <Icon size={16} className="text-slate-500 shrink-0" />
      <span className="text-slate-500 text-sm w-28 shrink-0">{label}</span>
      <span className="text-white text-sm">{value || '-'}</span>
    </div>
  );

  const StatCard = ({ icon: Icon, label, value, color, testId, className = '' }) => (
    <div className={`flex flex-col items-center justify-center py-5 px-2 ${className}`} data-testid={testId}>
      <Icon size={20} style={{ color }} className="mb-2" />
      <span className="text-2xl font-bold text-white font-mono">{value}</span>
      <span className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{label}</span>
    </div>
  );

  return (
    <div className="min-h-screen ept-bg-ambient pt-20 pb-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-slate-400 hover:text-white"
          data-testid="back-btn"
        >
          <ArrowLeft size={18} className="mr-2" />
          {isItalian ? 'Indietro' : 'Back'}
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#0a0d15] border border-white/10 overflow-hidden"
        >
          {/* Header */}
          <div className="relative h-24 bg-gradient-to-r from-[#00F0FF]/10 to-transparent">
            <div className="absolute -bottom-10 left-6">
              {profile.picture ? (
                <img
                  src={profile.picture}
                  alt=""
                  className="w-20 h-20 rounded-full border-4 border-[#0a0d15] object-cover"
                  data-testid="user-avatar"
                />
              ) : (
                <div className="w-20 h-20 rounded-full border-4 border-[#0a0d15] bg-[#050508] flex items-center justify-center" data-testid="user-avatar">
                  <User size={32} className="text-slate-600" />
                </div>
              )}
            </div>
          </div>

          {/* Name + Role */}
          <div className="pt-14 px-6 pb-2">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-bold text-white" data-testid="user-name">
                {profile.gamertag || profile.name || profile.email}
              </h1>
              <span className={`px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider border ${roleColors[profile.role] || roleColors.player}`} data-testid="user-role">
                {roleLabels[profile.role] || profile.role}
              </span>
              {profile.is_banned && (
                <span className="px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider border bg-[#FF1744]/20 text-[#FF1744] border-[#FF1744]/30">
                  Banned
                </span>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="px-6 pb-6">
            <InfoRow icon={Mail} label="Email" value={profile.email} />
            <InfoRow icon={Gamepad2} label="Gamertag" value={profile.gamertag} />
            <InfoRow icon={Shield} label={isItalian ? 'Ruolo Gioco' : 'Player Role'} value={profile.player_role} />
            <InfoRow icon={Phone} label={isItalian ? 'Telefono' : 'Phone'} value={profile.phone} />
            <InfoRow icon={Calendar} label={isItalian ? 'Anno Nascita' : 'Birth Year'} value={profile.birth_year} />
            <InfoRow icon={Coins} label="Coins" value={
              <span className="font-mono text-[#FFEA00]">{(profile.personal_coins || 0).toLocaleString()}</span>
            } />
            {club && (
              <InfoRow icon={Users} label="Club" value={
                <span className="flex items-center gap-2">
                  {club.name}
                  {club.captain_id === profile.user_id && <Crown size={14} className="text-[#FFEA00]" />}
                </span>
              } />
            )}
            <InfoRow icon={Calendar} label={isItalian ? 'Registrato' : 'Registered'} value={
              profile.created_at ? new Date(profile.created_at).toLocaleDateString('it-IT') : '-'
            } />
          </div>
        </motion.div>

        {/* Player Stats */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mt-4 bg-[#0a0d15] border border-white/10 overflow-hidden"
            data-testid="player-stats-section"
          >
            <div className="px-6 py-4 border-b border-white/5">
              <h2 className="text-sm font-bold uppercase tracking-widest text-slate-400">
                {isItalian ? 'Statistiche Giocatore' : 'Player Statistics'}
              </h2>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5 divide-x divide-white/5">
              <StatCard
                icon={Target}
                label={isItalian ? 'Gol' : 'Goals'}
                value={stats.goals}
                color="#00F0FF"
                testId="stat-goals"
              />
              <StatCard
                icon={Handshake}
                label="Assist"
                value={stats.assists}
                color="#00E676"
                testId="stat-assists"
              />
              <StatCard
                icon={ClipboardList}
                label={isItalian ? 'Presenze' : 'Apps'}
                value={stats.appearances}
                color="#FFEA00"
                testId="stat-appearances"
              />
              <StatCard
                icon={CircleAlert}
                label={isItalian ? 'Gialli' : 'Yellows'}
                value={stats.yellow_cards}
                color="#FFD600"
                testId="stat-yellows"
                className="hidden sm:flex"
              />
              <StatCard
                icon={Ban}
                label={isItalian ? 'Rossi' : 'Reds'}
                value={stats.red_cards}
                color="#FF1744"
                testId="stat-reds"
                className="hidden sm:flex"
              />
            </div>
            {/* Mobile: show cards row */}
            <div className="grid grid-cols-2 divide-x divide-white/5 border-t border-white/5 sm:hidden">
              <StatCard
                icon={CircleAlert}
                label={isItalian ? 'Gialli' : 'Yellows'}
                value={stats.yellow_cards}
                color="#FFD600"
                testId="stat-yellows-mobile"
              />
              <StatCard
                icon={Ban}
                label={isItalian ? 'Rossi' : 'Reds'}
                value={stats.red_cards}
                color="#FF1744"
                testId="stat-reds-mobile"
              />
            </div>
          </motion.div>
        )}

        {/* EA Pro Clubs stats (read-only) */}
        <EAPlayerStatsCard userId={userId} editable={false} isItalian={isItalian} />
      </div>
    </div>
  );
};

export default UserProfile;
